import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchChatConfig,
  fetchChatList,
  fetchChatMessages,
  openChat,
  sendChatMessage,
  shareChatContact,
} from '../../api/jadeChat/jadeChatApi';
import './jade-chat.css';
import { OPEN_JADE_CHAT_EVENT } from './openJadeChat';
import { rememberAiChat, track } from '../../utils/tracker';

const POLL_MS = 6000;
const GENERAL_STARTERS = [
  'Plan a weekend getaway for me',
  'Villas for 8 guests with a pool',
  'How does booking work?',
];

function shortDate(value) {
  if (!value) return '';
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The guest's bookings as options to tap (from show_my_bookings); tapping sends the choice. */
function BookingOptions({ bookings, onPick, disabled }) {
  return (
    <div className="jc-bookings" role="list">
      {bookings.map((b) => (
        <button
          type="button"
          role="listitem"
          key={b.id}
          className="jc-booking"
          disabled={disabled}
          onClick={() => onPick(`Booking #${b.id} · ${b.property} · ${shortDate(b.check_in)} – ${shortDate(b.check_out)}`)}
        >
          <span className="jc-booking-title">{b.property}</span>
          <span className="jc-booking-meta">
            {shortDate(b.check_in)} – {shortDate(b.check_out)}
            {b.guests ? ` · ${b.guests} guest${Number(b.guests) === 1 ? '' : 's'}` : ''}
          </span>
          <span className={`jc-booking-status is-${String(b.status).replace(/\s+/g, '-')}`}>
            #{b.id} · {b.status}
          </span>
        </button>
      ))}
    </div>
  );
}

// Booking support chat (My Bookings → "Chat about this stay").
const BOOKING_STARTERS = [
  'What time is check-in?',
  "What's the Wi-Fi password?",
  'Can I check in early?',
  'How do I reach the property?',
  "Something isn't right",
];

function propertyStarters(context) {
  return [
    context.checkIn && context.checkOut ? 'Is it available for my dates?' : 'When is it available?',
    context.checkIn && context.checkOut ? 'What is the total for my dates?' : 'How much does it cost per night?',
    'What amenities does it have?',
    'Is it pet-friendly?',
    'What is the cancellation policy?',
  ];
}

const formatInr = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;

/** Property page context (/property/:id?checkIn&checkOut&guest) for the assistant. */
function usePageContext() {
  const { pathname, search } = useLocation();
  return useMemo(() => {
    const match = pathname.match(/^\/property\/(\d+)/);
    if (!match) return { key: 'general', context: {} };
    const params = new URLSearchParams(search);
    const context = {
      propertyId: Number(match[1]),
      checkIn: params.get('checkIn') || null,
      checkOut: params.get('checkOut') || null,
      guests: params.get('guest') ? Number(params.get('guest')) : null,
    };
    return { key: `property:${context.propertyId}`, context };
  }, [pathname, search]);
}

/** Card links point at the public site; open same-site links in-app. */
function useOpenLink() {
  const navigate = useNavigate();
  return useCallback(
    (url) => {
      let target;
      try {
        target = new URL(url, window.location.origin);
      } catch {
        return;
      }
      // Only web links: never javascript:, data: or other schemes from a message card.
      if (target.protocol !== 'https:' && target.protocol !== 'http:') return;
      const path = `${target.pathname}${target.search}`;
      if (target.origin === window.location.origin && /^\/(property|request-booking|properties|login)\b/.test(target.pathname)) {
        navigate(path);
        return;
      }
      window.open(target.href, '_blank', 'noopener,noreferrer');
    },
    [navigate]
  );
}

export const CHAT_GUEST_KEY = 'jadecaps_chat_guest';
const ACTIVE_CHAT_KEY = 'jadecaps_chat_active';

// The open chat follows the guest across pages for this browser session.
function readActiveChat() {
  try {
    return Number(sessionStorage.getItem(ACTIVE_CHAT_KEY)) || null;
  } catch {
    return null;
  }
}

function writeActiveChat(id) {
  try {
    if (id) sessionStorage.setItem(ACTIVE_CHAT_KEY, String(id));
    else sessionStorage.removeItem(ACTIVE_CHAT_KEY);
  } catch {
    /* ignore */
  }
}

function formatWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/**
 * Hand the details the guest shared in chat to checkout (same tab, this session only) instead
 * of putting their phone / email in the link. Checkout fills empty fields and clears the key.
 */
function stashChatGuest(url, contact) {
  if (!contact) return;
  try {
    const target = new URL(url, window.location.origin);
    const match = target.pathname.match(/^\/request-booking\/(\d+)/);
    if (!match) return;
    const [firstName, ...rest] = String(contact.name || '').trim().split(/\s+/);
    sessionStorage.setItem(
      CHAT_GUEST_KEY,
      JSON.stringify({
        propertyId: match[1],
        firstName: firstName || '',
        lastName: rest.join(' '),
        email: contact.email || '',
        phone: String(contact.phone || '').replace(/\D/g, '').slice(-10),
      })
    );
  } catch {
    /* storage blocked — guest types details at checkout */
  }
}

function MessageText({ text }) {
  // Light formatting only: **bold** and line breaks; everything else stays plain text.
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="jc-text">
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>
      )}
    </p>
  );
}

function PropertyCards({ cards, onOpen }) {
  return (
    <div className={`jc-cards${cards.length > 1 ? ' is-row' : ''}`}>
      {cards.map((card) => {
        const nights =
          card.checkIn && card.checkOut
            ? Math.round((new Date(card.checkOut) - new Date(card.checkIn)) / 86400000)
            : null;
        return (
          <div className={`jc-card${card.kind === 'booking' ? ' is-booking' : ''}`} key={`${card.kind}-${card.propertyId}`}>
            <div className="jc-card-media">
              {card.image ? <img src={card.image} alt="" loading="lazy" /> : <span className="jc-card-media-empty" />}
              {card.kind === 'booking' ? <span className="jc-card-tag">Ready to book</span> : null}
            </div>
            <div className="jc-card-body">
              <p className="jc-card-title">{card.title}</p>
              <p className="jc-card-meta">
                {[card.area, card.sleeps ? `Sleeps ${card.sleeps}` : null].filter(Boolean).join(' · ')}
              </p>
              {card.totalPrice ? (
                <p className="jc-card-price">
                  {formatInr(card.totalPrice)}
                  <span>{nights ? ` for ${nights} night${nights > 1 ? 's' : ''}` : ' total'}</span>
                </p>
              ) : card.pricePerNight ? (
                <p className="jc-card-price">
                  {formatInr(card.pricePerNight)}
                  <span> / night onwards</span>
                </p>
              ) : null}
              {card.couponCode ? (
                <p className="jc-card-offer">
                  {card.couponCode} applied{card.discount ? ` · you save ${formatInr(card.discount)}` : ''}
                </p>
              ) : null}
              {card.available === false ? <p className="jc-card-unavail">Not available for these dates</p> : null}
              <div className="jc-card-actions">
                {card.kind === 'booking' ? (
                  <button type="button" className="jc-btn-primary" onClick={() => onOpen(card.url, { action: 'checkout', card })}>
                    {card.bookingMode === 'request' ? 'Request to book' : 'Continue to checkout'}
                  </button>
                ) : (
                  <>
                    <button type="button" className="jc-btn-ghost" onClick={() => onOpen(card.url, { action: 'view', card })}>
                      View
                    </button>
                    {card.bookUrl ? (
                      <button type="button" className="jc-btn-primary" onClick={() => onOpen(card.bookUrl, { action: 'book', card })}>
                        Book
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function senderLabel(message, assistantName) {
  if (message.senderType === 'customer') return 'yourself';
  if (message.senderType === 'admin') return message.senderName || 'the JadeCaps team';
  return assistantName;
}

/** Optional name + mobile form the assistant can show (e.g. when the team will follow up). */
function ContactForm({ onSubmit, onSkip, busy }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const submit = async () => {
    setError('');
    if (name.trim().length < 2) return setError('Please enter your name.');
    if (!/^[6-9]\d{9}$/.test(phone.replace(/\D/g, '').slice(-10))) return setError('Please enter a valid 10-digit mobile number.');
    try {
      await onSubmit({ name: name.trim(), phone: phone.replace(/\D/g, '').slice(-10), email: email.trim() || null });
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save your details. Please try again.');
    }
    return null;
  };
  return (
    <div className="jc-form">
      <p className="jc-form-title">Share your details so our team can reach you</p>
      <input placeholder="Your name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      <div className="jc-form-phone">
        <span>+91</span>
        <input
          placeholder="Mobile number"
          inputMode="numeric"
          value={phone}
          maxLength={14}
          onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s-]/g, ''))}
          autoComplete="tel-national"
        />
      </div>
      <input placeholder="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      {error ? <p className="jc-form-error">{error}</p> : null}
      <div className="jc-form-actions">
        <button type="button" className="jc-btn-ghost" onClick={onSkip} disabled={busy}>
          Not now
        </button>
        <button type="button" className="jc-btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : 'Share details'}
        </button>
      </div>
      <p className="jc-form-note">Only the JadeCaps team sees this. We won&apos;t share it.</p>
    </div>
  );
}

/**
 * "Ask Jade" — the AI booking assistant (PMS-API /concierge-web). Hidden unless enabled in
 * PMS-Admin → Concierge → AI settings. Staff can take a chat over from the admin inbox; their
 * replies arrive here by polling.
 */
export default function JadeChatWidget() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState({}); // conversationId -> { conversation, messages }
  const [activeId, setActiveIdState] = useState(readActiveChat);
  const [view, setView] = useState('chat'); // chat | list
  const [chatList, setChatList] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [skippedForms, setSkippedForms] = useState(() => new Set());
  const [replyTo, setReplyTo] = useState(null); // message being replied to
  const { context } = usePageContext();
  const setActiveId = useCallback((id) => {
    setActiveIdState(id);
    writeActiveChat(id);
  }, []);
  const navigateLink = useOpenLink();
  const listRef = useRef(null);
  const openingBookingRef = useRef(false);

  const chat = activeId ? chats[activeId] : null;
  const messages = useMemo(() => chat?.messages || [], [chat]);
  const conversation = chat?.conversation;
  const openLink = useCallback(
    (url, { action, card } = {}) => {
      if (action && conversation?.id) {
        rememberAiChat(conversation.id);
        track(action === 'view' ? 'chat_property_click' : 'chat_checkout_click', {
          conversationId: conversation.id,
          propertyId: card?.propertyId,
          action,
        });
      }
      stashChatGuest(url, conversation?.contact);
      navigateLink(url);
    },
    [conversation?.contact, conversation?.id, navigateLink]
  );

  useEffect(() => {
    fetchChatConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, []);

  // Login / logout (here or in another tab): drop what's on screen and load this identity's chats.
  // After logout the old chats stay with the account and are no longer visible in this browser.
  useEffect(() => {
    const reset = () => {
      setChats({});
      setActiveId(null);
      setChatList(null);
      setView('chat');
      setReplyTo(null);
      setSkippedForms(new Set());
    };
    const onStorage = (e) => {
      if (e.key === null || e.key === 'customerToken') reset();
    };
    window.addEventListener('jadecaps:auth-changed', reset);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('jadecaps:auth-changed', reset);
      window.removeEventListener('storage', onStorage);
    };
  }, [setActiveId]);

  // My Bookings → "Chat about this stay": open (or resume) that booking's support chat.
  useEffect(() => {
    const onOpen = (e) => {
      const bookingId = e?.detail?.bookingId || null;
      setOpen(true);
      setView('chat');
      setReplyTo(null);
      if (!bookingId) return;
      setError('');
      openingBookingRef.current = true;
      openChat({ bookingId })
        .then((data) => {
          if (!data?.conversation) return;
          setChats((prev) => ({ ...prev, [data.conversation.id]: { conversation: data.conversation, messages: data.messages || [] } }));
          setActiveId(data.conversation.id);
        })
        .catch((err) => {
          setError(err?.response?.data?.message || "Couldn't open the chat for this booking. Please try again.");
        })
        .finally(() => {
          openingBookingRef.current = false;
        });
    };
    window.addEventListener(OPEN_JADE_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_JADE_CHAT_EVENT, onOpen);
  }, [setActiveId]);

  // Resume the active chat (it follows the guest across pages), or start one for this page.
  useEffect(() => {
    if (!open || !config?.enabled || view !== 'chat') return undefined;
    if (activeId && chats[activeId]) return undefined;
    if (openingBookingRef.current) return undefined; // a booking chat is on its way
    let cancelled = false;
    setError('');
    const load = activeId ? fetchChatMessages(activeId) : openChat(context);
    load
      .then((data) => {
        if (cancelled || !data?.conversation) return;
        setChats((prev) => ({ ...prev, [data.conversation.id]: { conversation: data.conversation, messages: data.messages || [] } }));
        setActiveId(data.conversation.id);
      })
      .catch(() => {
        if (cancelled) return;
        if (activeId) setActiveId(null); // gone or not ours any more: start a fresh one
        else setError("Chat isn't available right now. Please try again shortly.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, config?.enabled, view, activeId, chats, context, setActiveId]);

  const startNewChat = async () => {
    setView('chat');
    setError('');
    setReplyTo(null);
    try {
      const data = await openChat({ ...context, forceNew: true });
      setChats((prev) => ({ ...prev, [data.conversation.id]: { conversation: data.conversation, messages: data.messages || [] } }));
      setActiveId(data.conversation.id);
    } catch {
      setError("Couldn't start a new chat. Please try again.");
    }
  };

  const showChatList = () => {
    setView('list');
    setChatList(null);
    fetchChatList()
      .then((rows) => setChatList(Array.isArray(rows) ? rows : []))
      .catch(() => setChatList([]));
  };

  const pickChat = (id) => {
    setReplyTo(null);
    setActiveId(id);
    setView('chat');
  };

  // Staff replies (after a takeover) arrive by polling while the panel is open.
  useEffect(() => {
    if (!open || !conversation?.id) return undefined;
    const timer = setInterval(() => {
      if (sending) return;
      const lastId = messages.length ? messages[messages.length - 1].id : 0;
      fetchChatMessages(conversation.id, lastId)
        .then((data) => {
          if (!data?.messages?.length && data?.conversation?.handoffStatus === conversation.handoffStatus) return;
          setChats((prev) => {
            const current = prev[conversation.id];
            if (!current) return prev;
            const known = new Set(current.messages.map((m) => m.id));
            const fresh = (data.messages || []).filter((m) => !known.has(m.id));
            return { ...prev, [conversation.id]: { conversation: data.conversation || current.conversation, messages: [...current.messages, ...fresh] } };
          });
        })
        .catch((err) => {
          // Not ours any more (e.g. logged out in another tab): leave it and start fresh.
          if (err?.response?.status === 404) {
            setChats((prev) => {
              const next = { ...prev };
              delete next[conversation.id];
              return next;
            });
            setActiveId(null);
          }
        });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [open, conversation?.id, conversation?.handoffStatus, messages, sending, setActiveId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, sending, open]);

  const send = async (text) => {
    const body = String(text || '').trim();
    if (!body || sending || !conversation?.id) return;
    setDraft('');
    setError('');
    setSending(true);
    const quoting = replyTo;
    setReplyTo(null);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      senderType: 'customer',
      body,
      messageType: 'text',
      pending: true,
      replyTo: quoting ? { id: quoting.id, senderName: senderLabel(quoting, name), body: quoting.body } : null,
    };
    const id = conversation.id;
    setChats((prev) => ({ ...prev, [id]: { ...prev[id], messages: [...prev[id].messages, optimistic] } }));
    const page = context.propertyId
      ? { propertyId: context.propertyId, checkIn: context.checkIn, checkOut: context.checkOut, guests: context.guests }
      : { propertyId: null };
    rememberAiChat(id);
    try {
      const data = await sendChatMessage(id, body, quoting && Number.isFinite(Number(quoting.id)) ? Number(quoting.id) : null, page);
      setChats((prev) => {
        const current = prev[id];
        const kept = current.messages.filter((m) => m.id !== optimistic.id);
        const known = new Set(kept.map((m) => m.id));
        return {
          ...prev,
          [id]: {
            conversation: data.conversation || current.conversation,
            messages: [...kept, ...(data.messages || []).filter((m) => !known.has(m.id))],
          },
        };
      });
    } catch (err) {
      setChats((prev) => ({
        ...prev,
        [id]: { ...prev[id], messages: prev[id].messages.filter((m) => m.id !== optimistic.id) },
      }));
      setDraft(body);
      setReplyTo(quoting);
      setError(err?.response?.data?.message || "Your message didn't go through. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const shareContact = async (details) => {
    setSending(true);
    try {
      const id = conversation.id;
      const data = await shareChatContact(id, details);
      setChats((prev) => {
        const current = prev[id];
        const known = new Set(current.messages.map((m) => m.id));
        return {
          ...prev,
          [id]: {
            conversation: data.conversation || current.conversation,
            messages: [...current.messages, ...(data.messages || []).filter((m) => !known.has(m.id))],
          },
        };
      });
    } finally {
      setSending(false);
    }
  };

  if (!config?.enabled) return null;

  const name = config.assistantName || 'Jade';
  const onPropertyPage = Boolean(context.propertyId);
  const propertyTitle = conversation?.propertyTitle || null;
  const starters = conversation?.bookingId
    ? BOOKING_STARTERS
    : onPropertyPage
    ? propertyStarters(context)
    : conversation?.loggedIn
      ? ['Help with my booking', ...GENERAL_STARTERS]
      : GENERAL_STARTERS;
  const loginHref = `/login?redirect=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
  const withStaff = conversation && conversation.handoffStatus && conversation.handoffStatus !== 'ai';
  const onlyWelcome = messages.length <= 1 && messages.every((m) => m.senderType !== 'customer');

  return (
    <>
      <button
        type="button"
        className={`jc-launcher${open ? ' is-open' : ''}`}
        onClick={() => {
          track('chat_launcher_click', { open: !open, propertyId: context.propertyId, conversationId: activeId });
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-controls="jade-chat-panel"
      >
        {open ? (
          <span aria-hidden="true">×</span>
        ) : (
          <>
            <span className="jc-launcher-spark" aria-hidden="true">
              <img src="/images/jadecaps-agent-emoji.webp" alt="" />
            </span>
            <span className="jc-launcher-label">{onPropertyPage ? 'Ask about this stay' : `Ask ${name}`}</span>
          </>
        )}
      </button>

      {open && (
        <div className="jc-panel" id="jade-chat-panel" role="dialog" aria-label={`Chat with ${name}`}>
          <div className="jc-head">
            <div className="jc-avatar" aria-hidden="true">
              {String(name).charAt(0).toUpperCase()}
              <span className="jc-avatar-dot" />
            </div>
            <div>
              <p className="jc-head-title">{name}</p>
              <p className="jc-head-sub">
                {withStaff
                  ? 'JadeCaps team'
                  : conversation?.bookingId
                    ? `Your stay · Booking #${conversation.bookingId}`
                    : propertyTitle
                      ? `Ask me about ${propertyTitle}`
                      : 'AI stay assistant · replies in seconds'}
              </p>
            </div>
            <div className="jc-head-actions">
              <button
                type="button"
                className={`jc-head-btn${view === 'list' ? ' is-on' : ''}`}
                onClick={() => (view === 'list' ? setView('chat') : showChatList())}
                aria-label={view === 'list' ? 'Back to chat' : 'Your chats'}
                title={view === 'list' ? 'Back to chat' : 'Your chats'}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 6h16M4 12h16M4 18h10" />
                </svg>
              </button>
              <button type="button" className="jc-head-btn" onClick={startNewChat} aria-label="New chat" title="New chat">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
              <button type="button" className="jc-head-close" onClick={() => setOpen(false)} aria-label="Close chat">
                ×
              </button>
            </div>
          </div>

          {view === 'list' ? (
            <div className="jc-chatlist">
              <p className="jc-chatlist-title">Your chats</p>
              {chatList === null ? <p className="jc-status">Loading…</p> : null}
              {chatList && !chatList.length ? <p className="jc-status">No chats yet.</p> : null}
              {(chatList || []).map((row) => (
                <button
                  type="button"
                  key={row.id}
                  className={`jc-chatlist-item${row.id === activeId ? ' is-active' : ''}`}
                  onClick={() => pickChat(row.id)}
                >
                  <span className="jc-chatlist-top">
                    <strong>{row.title}</strong>
                    <span>{formatWhen(row.lastMessageAt)}</span>
                  </span>
                  <span className="jc-chatlist-preview">{row.lastMessagePreview || 'No messages yet'}</span>
                </button>
              ))}
              <button type="button" className="jc-btn-primary jc-chatlist-new" onClick={startNewChat}>
                Start a new chat
              </button>
            </div>
          ) : (
          <>
          {withStaff ? (
            <div className="jc-banner">
              {conversation.aiEnabled === false
                ? 'A JadeCaps team member is handling this chat and will reply here.'
                : conversation.handoffStatus === 'human'
                  ? `A JadeCaps team member is handling this chat. If they're away, ${name} will help you meanwhile.`
                  : `Our team has been notified and will reply here. ${name} can still help you meanwhile.`}
            </div>
          ) : null}

          <div className="jc-list" ref={listRef}>
            {!chat && !error ? <p className="jc-status">Starting chat…</p> : null}
            {messages.map((m, index) => {
              if (m.senderType === 'system') {
                return (
                  <div key={m.id} className="jc-notice">
                    <span>{m.body}</span>
                  </div>
                );
              }
              const mine = m.senderType === 'customer';
              const cards = Array.isArray(m.metadata?.cards) ? m.metadata.cards : [];
              const isLast = index === messages.length - 1;
              const bookingOptions = Array.isArray(m.metadata?.bookingOptions) ? m.metadata.bookingOptions : [];
              const showLogin = m.metadata?.loginPrompt && !conversation?.loggedIn && isLast;
              const showForm =
                m.metadata?.contactForm &&
                !conversation?.contactShared &&
                !skippedForms.has(m.id) &&
                index === messages.length - 1;
              return (
                <React.Fragment key={m.id}>
                  <div className={`jc-msg ${mine ? 'is-mine' : 'is-theirs'}${m.pending ? ' is-pending' : ''}`}>
                    {!mine && m.senderType === 'admin' ? <span className="jc-sender">{m.senderName || 'JadeCaps team'}</span> : null}
                    {m.replyTo?.body ? (
                      <div className="jc-quote">
                        <span>{m.replyTo.senderType === 'customer' ? 'You' : m.replyTo.senderName || name}</span>
                        <p>{m.replyTo.body}</p>
                      </div>
                    ) : null}
                    {m.body ? <MessageText text={m.body} /> : null}
                    {cards.length ? <PropertyCards cards={cards} onOpen={openLink} /> : null}
                    {bookingOptions.length && isLast ? (
                      <BookingOptions bookings={bookingOptions} onPick={send} disabled={sending} />
                    ) : null}
                    {showLogin ? (
                      <button type="button" className="jc-btn-primary jc-login-btn" onClick={() => navigateLink(loginHref)}>
                        Log in to see your bookings
                      </button>
                    ) : null}
                    {m.body && !m.pending ? (
                      <button type="button" className="jc-reply-btn" onClick={() => setReplyTo(m)} aria-label="Reply to this message">
                        Reply
                      </button>
                    ) : null}
                  </div>
                  {showForm ? (
                    <ContactForm
                      busy={sending}
                      onSubmit={shareContact}
                      onSkip={() => {
                        track('contact_form_skipped', { conversationId: conversation?.id });
                        setSkippedForms((prev) => new Set(prev).add(m.id));
                      }}
                    />
                  ) : null}
                </React.Fragment>
              );
            })}
            {sending ? (
              <div className="jc-msg is-theirs jc-typing" aria-label={`${name} is typing`}>
                <span />
                <span />
                <span />
              </div>
            ) : null}
            {onlyWelcome && chat ? (
              <div className="jc-starters">
                {starters.map((s) => (
                  <button type="button" key={s} onClick={() => send(s)} disabled={sending}>
                    {s}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {error ? <p className="jc-error" role="alert">{error}</p> : null}

          {replyTo ? (
            <div className="jc-replying">
              <div>
                <span>Replying to {senderLabel(replyTo, name)}</span>
                <p>{replyTo.body}</p>
              </div>
              <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
                ×
              </button>
            </div>
          ) : null}
          <div className="jc-compose">
            <textarea
              rows={1}
              placeholder={
                withStaff && conversation.aiEnabled !== false
                  ? 'Ask anything while you wait…'
                  : conversation?.bookingId
                    ? 'Ask about your stay…'
                    : onPropertyPage
                      ? 'Ask about this stay…'
                      : 'Ask about stays, dates, prices…'
              }
              value={draft}
              maxLength={1000}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(draft);
                }
              }}
              disabled={!chat}
            />
            <button type="button" onClick={() => send(draft)} disabled={!chat || sending || !draft.trim()} aria-label="Send">
              ➤
            </button>
          </div>
          <p className="jc-foot">AI can make mistakes — prices and availability are confirmed at checkout.</p>
          </>
          )}
        </div>
      )}
    </>
  );
}
